import { Bell, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useAlertCounts, useAlertsSubscription } from '@/hooks/useAlerts';
import { AlertsPanel } from './AlertsPanel';
import { cn } from '@/lib/utils';

interface AlertBadgeProps {
  className?: string;
}

export function AlertBadge({ className }: AlertBadgeProps) {
  const { data: counts } = useAlertCounts();
  useAlertsSubscription(); // Enable real-time updates

  const hasAlerts = counts && counts.total > 0;
  const hasCritical = counts && counts.critical > 0;
  const hasHigh = counts && (counts.critical > 0 || counts.high > 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn("relative", className)}
          aria-label={`Alerts${hasAlerts ? ` (${counts.total} active)` : ''}`}
        >
          {hasCritical ? (
            <AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />
          ) : (
            <Bell className={cn('h-5 w-5', hasHigh && 'text-signal-caution')} aria-hidden="true" />
          )}
          {hasAlerts && (
            <span
              className={cn(
                'absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white',
                hasCritical ? 'bg-destructive' : hasHigh ? 'bg-signal-caution' : 'bg-primary'
              )}
            >
              {counts.total > 99 ? '99+' : counts.total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[80] w-96 p-0">
        <AlertsPanel />
      </PopoverContent>
    </Popover>
  );
}
